import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import ContactDynamicMultiSelectInput from 'components/party/ContactDynamicMultiSelectInput';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { ListedIn } from 'types/back-office-settings';
import { RelationshipTypeCode } from 'types/contacts';
import { ContactFormState } from './useContactForm';

/** About: the relationship type and the relationship partners, managers and client assistants. */
export const AboutFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue, getValues } = form.methods;
    const {
        administratorFieldLabel,
        isAdministratorFieldEnabled,
        isAdministratorRequired,
        isRelationshipManagerRequired,
        relationshipType,
        relationshipTypeOptions,
        relationshipTypeRightLabelComponent,
    } = form;

    return (
        <>
            {/* RadioInput always validates as required, but this one is not marked so: see ChooseTypeFields. */}
            <CreateDialogRequiredBlock when>
                <RadioInput
                    setValue={setValue}
                    control={control}
                    label="contacts.about.relationship-type"
                    name="relationshipType"
                    options={relationshipTypeOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    getOptionDisabled="disabled"
                    tooltip={relationshipTypeRightLabelComponent}
                    row
                    onChange={(option) => {
                        // Becoming a client without a VAT number reopens the question:
                        // clear the opt-out so the user states a number or ticks it again.
                        if (option?.value === RelationshipTypeCode.CLIENT && !getValues('vatRegNum')) {
                            setValue('notVatLiable', false);
                        }
                    }}
                />
            </CreateDialogRequiredBlock>
            <ContactDynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="contacts.about.relationship-partner"
                name="partners"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_PARTNER } },
                }}
                placeholder="contacts.about.relationship-partner"
                required={
                    relationshipType === RelationshipTypeCode.CLIENT || relationshipType === RelationshipTypeCode.LEAD
                }
            />
            <ContactDynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="contacts.about.relationship-manager"
                name="managers"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_MANAGER } },
                }}
                placeholder="contacts.about.relationship-manager"
                required={isRelationshipManagerRequired}
            />
            {isAdministratorFieldEnabled ? (
                <ContactDynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label={administratorFieldLabel}
                    shouldTranslateLabel={false}
                    name="administrators"
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_ASSISTANT } },
                    }}
                    placeholder={administratorFieldLabel}
                    shouldTranslatePlaceholder={false}
                    required={isAdministratorRequired}
                />
            ) : null}
        </>
    );
};
